import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Cart from './Cart';
import OrderTracker from './OrderTracker';

// Lazy load heavy 3D viewer — only downloads when a 3D item is visible (~500KB saved on initial load)
const FoodViewer = lazy(() => import('./FoodViewer'));

const PublicMenu = () => {
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const tableNumber = searchParams.get('table');

  // --- CORE DATA STATE ---
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // --- CART STATE ---
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // --- ORDER TRACKING STATE ---
  const [activeOrderIds, setActiveOrderIds] = useState([]);
  const [showOrderTracker, setShowOrderTracker] = useState(false);

  // --- UI STATE ---
  const [selectedItemForVariant, setSelectedItemForVariant] = useState(null);

  // --- SMART UPSELL STATE ---
  const [upsellItem, setUpsellItem] = useState(null); // The main item triggering the upsell
  const [upsellCandidates, setUpsellCandidates] = useState([]); // The recommended items found
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  // --- CACHE HELPERS for instant loading on slow networks ---
  const CACHE_KEY_RESTO = `tablear_cache_resto_${restaurantId}`;
  const CACHE_KEY_MENU = `tablear_cache_menu_${restaurantId}`;
  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  const getCached = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp > CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch { return null; }
  };

  const setCache = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch { /* localStorage full – ignore */ }
  };

  // --- DATA FETCHING: Dual strategy (Vercel CDN proxy → Supabase fallback) ---
  const fetchRestaurantData = async (retryCount = 0) => {
    const MAX_RETRIES = 3;

    // On first call only: load from cache for instant display
    if (retryCount === 0) {
      const cachedResto = getCached(CACHE_KEY_RESTO);
      const cachedMenu = getCached(CACHE_KEY_MENU);
      if (cachedResto && cachedMenu) {
        setRestaurant(cachedResto);
        setMenuItems(cachedMenu);
        setCategories(["All", ...new Set(cachedMenu.map(item => item.category))]);
        setLoading(false);
        // Still fetch fresh data silently in background
      }
    }

    try {
      let resto, itemsWithRatings;

      // STRATEGY 1: Try Vercel API proxy first (goes through Vercel CDN — reliable on mobile data)
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        const apiRes = await fetch(`/api/menu?id=${restaurantId}`, {
          signal: controller.signal,
          headers: { 'Accept': 'application/json' },
        });
        clearTimeout(timeoutId);

        if (apiRes.ok) {
          const data = await apiRes.json();
          resto = data.restaurant;
          itemsWithRatings = data.menuItems;
        } else {
          throw new Error('API proxy returned error');
        }
      } catch (proxyErr) {
        // STRATEGY 2: Fall back to direct Supabase call
        console.log('Proxy failed, falling back to direct Supabase:', proxyErr.message);

        const { data: restoData, error: restoError } = await supabase
          .from('restaurants').select('*').eq('id', restaurantId).single();
        if (restoError) throw restoError;

        const { data: items, error: itemsError } = await supabase
          .from('menu_items').select('*')
          .eq('restaurant_id', restaurantId)
          .order('created_at', { ascending: false });
        if (itemsError) throw itemsError;

        const { data: reviews } = await supabase
          .from('reviews').select('menu_item_id, rating')
          .eq('restaurant_id', restaurantId);

        resto = restoData;
        itemsWithRatings = (items || []).map(item => {
          const itemReviews = reviews?.filter(r => String(r.menu_item_id) === String(item.id)) || [];
          const avgRating = itemReviews.length > 0
            ? itemReviews.reduce((acc, r) => acc + r.rating, 0) / itemReviews.length
            : 0;
          return { ...item, average_rating: avgRating, review_count: itemReviews.length };
        });
      }

      // Update state & cache
      setRestaurant(resto);
      setMenuItems(itemsWithRatings);
      setCategories(["All", ...new Set(itemsWithRatings.map(item => item.category))]);
      setCache(CACHE_KEY_RESTO, resto);
      setCache(CACHE_KEY_MENU, itemsWithRatings);
      setLoadError(false);
      setLoading(false);
    } catch (err) {
      console.error(`Menu load attempt ${retryCount + 1} failed:`, err);

      // Auto-retry with exponential backoff
      if (retryCount < MAX_RETRIES) {
        const delay = Math.pow(2, retryCount + 1) * 1000; // 2s, 4s, 8s
        console.log(`Retrying in ${delay / 1000}s...`);
        await new Promise(r => setTimeout(r, delay));
        return fetchRestaurantData(retryCount + 1);
      }

      // All retries failed – show error only if no cached data is being displayed
      setLoadError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1. Load active orders from Session
    const storedOrders = sessionStorage.getItem(`active_orders_${restaurantId}`);
    if (storedOrders) {
      try { setActiveOrderIds(JSON.parse(storedOrders)); } catch (e) { console.error(e); }
    }

    // 2. Analytics
    const logVisit = async () => {
      if (!restaurantId) return;
      const sessionKey = `tablear_visited_${restaurantId}`;
      if (!sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, 'true');
        supabase.from('analytics_events').insert([{ restaurant_id: restaurantId, event_type: 'scan' }]);
      }
    };

    // 3. Fetch Data & Log Visit
    if (restaurantId) {
      fetchRestaurantData();
      logVisit();
    }
  }, [restaurantId]);

  // --- CORE CART LOGIC (Reused by clicks & upsells) ---
  const performAddToCart = (item, variant = null) => {
    setCartItems(prev => {
      const finalPrice = variant ? parseFloat(variant.price) : parseFloat(item.price);
      const finalName = variant ? `${item.name} (${variant.name})` : item.name;
      const variantName = variant ? variant.name : null;

      const existingIndex = prev.findIndex(i => i.id === item.id && i.variantName === variantName);

      if (existingIndex > -1) {
        const newCart = [...prev];
        newCart[existingIndex] = { ...newCart[existingIndex], quantity: newCart[existingIndex].quantity + 1 };
        return newCart;
      } else {
        return [...prev, {
          id: item.id,
          name: finalName,
          price: finalPrice,
          quantity: 1,
          variantName: variantName,
          originalItem: item
        }];
      }
    });

    // Analytics
    supabase.from('analytics_events').insert([{ restaurant_id: restaurantId, event_type: 'add_to_cart', item_id: item.id }]);
  };

  // --- CLICK HANDLER (The Interceptor) ---
  const handleItemClick = (item) => {
    if (!item.is_available) return;
    if (!restaurant?.is_pro) return;

    // 1. If it has variants, open variant selector (Upsell check happens AFTER variant selection)
    if (item.variants && item.variants.length > 0) {
      setSelectedItemForVariant(item);
      return;
    }

    // 2. Add the main item to cart immediately
    performAddToCart(item);

    // 3. Check for Smart Upsells
    checkForUpsells(item);
  };

  const handleVariantSelect = (item, variant) => {
    // 1. Add variant to cart
    performAddToCart(item, variant);
    setSelectedItemForVariant(null);

    // 2. Check for Smart Upsells
    checkForUpsells(item);
  };

  // --- UPSELL LOGIC ---
  const checkForUpsells = (item) => {
    // Safety check: ensure related_items exists and is an array
    const relatedIds = item.related_items || [];

    if (relatedIds.length > 0) {
      // Find the actual item objects for these IDs
      // We convert IDs to strings to ensure matching works regardless of data type (int vs string)
      const recommendations = menuItems.filter(i =>
        relatedIds.map(String).includes(String(i.id)) &&
        i.is_available &&
        i.id !== item.id // Don't recommend itself
      );

      if (recommendations.length > 0) {
        setUpsellItem(item);
        setUpsellCandidates(recommendations);
        setShowUpsellModal(true);
      }
    }
  };

  const handleAddUpsell = (item) => {
    performAddToCart(item);
    closeUpsell(); // <--- CHANGED: Immediately close modal after adding
  };

  const closeUpsell = () => {
    setShowUpsellModal(false);
    setUpsellItem(null);
    setUpsellCandidates([]);
  };

  // --- ORDER HANDLER ---
  const handleOrderPlaced = (newOrderId) => {
    const newOrderList = [...activeOrderIds, newOrderId];
    setActiveOrderIds(newOrderList);
    sessionStorage.setItem(`active_orders_${restaurantId}`, JSON.stringify(newOrderList));
    setCartItems([]);
    setShowCart(false);
    setShowOrderTracker(true);
  };

  // --- RENDER HELPERS ---
  if (loading) return (
    <div
      className="min-h-screen font-sans text-gray-100 flex flex-col"
      style={{ background: 'radial-gradient(circle at 50% -20%, rgba(238,91,43,0.25) 0%, #0f0f11 80%)' }}
    >
      {/* Shimmer animation style */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%);
          background-size: 400px 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>

      {/* SKELETON: Hero Banner */}
      <div className="relative pt-6 px-4 z-10">
        <div className="rounded-[2.5rem] p-8 md:p-12 overflow-hidden relative mx-auto max-w-5xl bg-white/[0.04] border border-white/[0.06] backdrop-blur-sm">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Logo skeleton */}
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-white/[0.06] shimmer flex-shrink-0" />
            <div className="flex flex-col items-center md:items-start gap-4 flex-1 w-full">
              {/* Title skeleton */}
              <div className="h-10 md:h-14 w-72 md:w-96 rounded-2xl bg-white/[0.06] shimmer" />
              {/* Badges skeleton */}
              <div className="flex items-center gap-3">
                <div className="h-7 w-24 rounded-full bg-white/[0.06] shimmer" />
                <div className="h-7 w-40 rounded-full bg-white/[0.06] shimmer" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 container mx-auto max-w-5xl px-4 relative z-20 mt-8">

        {/* SKELETON: Sticky Nav (Search + Categories) */}
        <div className="py-4 px-4 mb-8 bg-white/[0.03] border border-white/[0.05] rounded-[2rem]">
          {/* Search bar skeleton */}
          <div className="h-14 w-full rounded-2xl bg-white/[0.06] shimmer mb-6" />
          {/* Category pills skeleton */}
          <div className="flex gap-3 overflow-hidden">
            {[80, 100, 90, 70, 110, 85].map((w, i) => (
              <div key={i} className="h-12 rounded-full bg-white/[0.06] shimmer flex-shrink-0" style={{ width: `${w}px` }} />
            ))}
          </div>
        </div>

        {/* SKELETON: Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="rounded-[2rem] p-4 bg-white/[0.04] border border-white/[0.06]">
              {/* Image skeleton */}
              <div className="aspect-[4/3] rounded-[1.5rem] bg-white/[0.06] shimmer mb-5" />
              {/* Title skeleton */}
              <div className="h-6 w-3/4 rounded-lg bg-white/[0.06] shimmer mb-3 mx-1" />
              {/* Description skeleton */}
              <div className="h-4 w-full rounded-lg bg-white/[0.06] shimmer mb-2 mx-1" />
              <div className="h-4 w-2/3 rounded-lg bg-white/[0.06] shimmer mb-5 mx-1" />
              {/* Price + button skeleton */}
              <div className="flex justify-between items-center px-1 pb-1">
                <div className="h-7 w-20 rounded-lg bg-white/[0.06] shimmer" />
                <div className="w-10 h-10 rounded-full bg-white/[0.06] shimmer" />
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
  if (loadError || !restaurant) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#0f0f11] gap-4 px-6 text-center">
      <span className="text-5xl">📡</span>
      <h2 className="text-white text-xl font-bold">{loadError ? "Couldn't load the menu" : "Restaurant not found"}</h2>
      {loadError && (
        <>
          <p className="text-gray-400 text-sm max-w-xs">This can happen on slower networks. Please check your connection and try again.</p>
          <button
            onClick={fetchRestaurantData}
            className="mt-2 bg-white text-black px-8 py-3 rounded-full font-bold text-sm hover:bg-gray-200 active:scale-95 transition-all shadow-lg"
          >
            🔄 Retry
          </button>
        </>
      )}
    </div>
  );

  const themeColor = restaurant.primary_color || '#ee5b2b';
  const showPromo = restaurant.promo_active && restaurant.promo_text;
  const instagram = restaurant.social_links?.instagram;
  const facebook = restaurant.social_links?.facebook;

  const hotItems = menuItems.filter(i => i.is_todays_special && i.is_available);
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = activeCategory === "All" || item.category === activeCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Helper for Auto-Emojis
  const getCategoryEmoji = (cat) => {
    const lower = cat.toLowerCase();
    if (lower.includes('start') || lower.includes('appetiz')) return '🥗';
    if (lower.includes('main') || lower.includes('course') || lower.includes('entree')) return '🥘';
    if (lower.includes('burger') || lower.includes('sandwich')) return '🍔';
    if (lower.includes('pizza')) return '🍕';
    if (lower.includes('drink') || lower.includes('beverage') || lower.includes('cocktail')) return '🍹';
    if (lower.includes('dessert') || lower.includes('sweet')) return '🍰';
    if (lower.includes('coffee') || lower.includes('tea')) return '☕';
    return '🍽️';
  };

  return (
    <div
      className="min-h-screen font-sans text-gray-100 flex flex-col transition-colors duration-700"
      style={{
        background: `radial-gradient(circle at 50% -20%, ${themeColor}40 0%, #0f0f11 80%)`
      }}
    >

      {/* 1. PROMO BANNER */}
      {showPromo && (
        <div className="bg-gray-900 text-white text-center py-3 px-4 text-xs font-bold tracking-wide animate-pulse shadow-md relative z-20">
          📣 {restaurant.promo_text}
        </div>
      )}

      {/* 2. HERO BANNER - Floating Glass */}
      <div className="relative pt-6 px-4 z-10 block">
        <div className="glass-panel rounded-[2.5rem] p-8 md:p-12 overflow-hidden relative shadow-2xl mx-auto max-w-5xl group transform transition-transform duration-700 hover:scale-[1.01]">
          {/* Dynamic Background Glow derived from Theme Color */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-white/10 to-transparent opacity-30 blur-3xl rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-black/40 to-transparent"></div>

          <div className="flex flex-col md:flex-row justify-between items-center relative z-20 gap-6">
            <div className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
              <div className="w-28 h-28 md:w-36 md:h-36 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-5xl shadow-2xl border border-white/20 overflow-hidden transform hover:scale-110 hover:rotate-3 transition-all duration-500">
                {restaurant.logo_url ? <img src={restaurant.logo_url} alt="Logo" className="w-full h-full object-contain" /> : <span>🍽️</span>}
              </div>
              <div>
                <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-400 leading-tight drop-shadow-sm font-serif mb-3 tracking-tight">{restaurant.name}</h1>
                <div className="flex items-center justify-center md:justify-start gap-3 flex-wrap">
                  <span className="glass px-4 py-1.5 rounded-full text-xs font-bold border-white/10 uppercase tracking-widest text-white/90">OPEN NOW</span>
                  <span className="text-white/40">•</span>
                  <span className="text-sm font-medium text-gray-300">Premium AR Experience</span>
                  {tableNumber && (
                    <span className="bg-white/10 px-4 py-1.5 rounded-full text-xs font-bold border border-white/10 uppercase tracking-widest flex items-center gap-1 animate-fade-in text-white/90 shadow-sm ml-2 backdrop-blur-md">
                      📍 Table {tableNumber}
                    </span>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="flex-1 container mx-auto max-w-5xl px-4 relative z-20 mt-8">

        {/* 3. TODAY'S HOT LIST */}
        {hotItems.length > 0 && (
          <div className="mb-10 animate-fade-in">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3 drop-shadow-md font-serif px-2">
              <span className="text-2xl animate-pulse">🔥</span> Chef's Highlights
            </h2>
            <div className="flex gap-6 overflow-x-auto pb-8 scrollbar-hide px-2">
              {hotItems.map(item => (
                <div key={item.id} className="min-w-[280px] glass rounded-[2.5rem] p-4 shadow-2xl flex flex-col hover:-translate-y-2 transition-transform duration-500 group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

                  <div className="h-48 bg-gray-900/50 rounded-[2rem] mb-4 overflow-hidden relative shadow-inner">
                    <img src={item.image_url} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" alt={item.name} />
                    {item.variants?.length > 0 && <span className="absolute bottom-3 right-3 glass text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider backdrop-blur-md">Variations</span>}
                  </div>
                  <h3 className="font-bold text-xl truncate text-white font-serif px-2 mb-1">{item.name}</h3>
                  <p className="text-gray-400 text-xs px-2 mb-3 line-clamp-1">{item.description}</p>

                  <div className="flex justify-between items-center mt-auto pt-2 px-2 pb-2">
                    <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-200 text-xl">
                      {item.variants?.length > 0 ? `From ₹${Math.min(...item.variants.map(v => v.price))}` : `₹${item.price}`}
                    </span>
                    {restaurant.is_pro && (
                      <button onClick={() => handleItemClick(item)} className="bg-white text-black w-10 h-10 rounded-full text-2xl font-normal flex items-center justify-center pb-1 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:scale-110 transition-all active:scale-95">+</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. STICKY NAV & SEARCH - Glassmorphism */}
        <div className="sticky top-2 z-30 py-4 px-4 transition-all duration-300 mb-8 backdrop-blur-xl bg-[#0f0f11]/80 border border-white/5 rounded-[2rem] shadow-2xl">
          <div className="container mx-auto max-w-5xl">
            {/* Search Bar */}
            <div className="relative mb-6">
              <input
                type="text" placeholder="Search for dishes..."
                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 font-medium outline-none focus:bg-white/10 focus:border-white/20 transition-all shadow-inner"
                style={{ '--tw-ring-color': themeColor }}
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>

            {/* Category Pills - Glass Style */}
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map(category => (
                <button
                  key={category} onClick={() => setActiveCategory(category)}
                  className={`px-6 py-3 rounded-full font-bold text-sm whitespace-nowrap transition-all border active:scale-95 flex items-center gap-2 ${activeCategory === category ? 'bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.4)] border-transparent' : 'glass text-gray-300 hover:bg-white/10 border-white/5'}`}
                >
                  <span>{getCategoryEmoji(category)}</span>
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 5. MENU GRID - Dark Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24">
          {filteredItems.map(item => (
            <div key={item.id} className={`glass rounded-[2rem] p-4 relative group transition-all duration-300 ${!item.is_available ? 'opacity-60 grayscale' : 'hover:bg-white/5 hover:border-white/20 hover:shadow-2xl'}`}>

              <div className="relative aspect-[4/3] bg-gray-900/50 rounded-[1.5rem] overflow-hidden mb-5 shadow-inner">
                {/* Viewer */}
                {item.is_available ? (
                  item.model_url ? (
                    <Suspense fallback={<img src={item.image_url} loading="lazy" decoding="async" className="w-full h-full object-cover" alt={item.name} />}>
                      <FoodViewer modelSrc={item.model_url} imageSrc={item.image_url} itemName={item.name} itemId={item.id} restaurantId={restaurantId} themeColor={themeColor} />
                    </Suspense>
                  ) : (
                    <img src={item.image_url} loading="lazy" decoding="async" className="w-full h-full object-cover" alt={item.name} />
                  )
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
                    <span className="font-bold text-white bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md text-sm border border-white/10">Out of Stock</span>
                    <img src={item.image_url} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover -z-10 opacity-20" alt={item.name} />
                  </div>
                )}

                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2 items-start pointer-events-none z-10">
                  <span className="bg-black/40 backdrop-blur-md text-[10px] font-bold px-3 py-1.5 rounded-full text-white shadow-md uppercase tracking-wider border border-white/10">{item.category}</span>

                  {item.dietary_badge && item.dietary_badge !== 'None' && (
                    <span className={`text-[10px] font-bold px-3 py-1.5 rounded-full text-white shadow-md uppercase tracking-wider ${item.dietary_badge === 'Veg' ? 'bg-green-500/80' :
                      item.dietary_badge === 'Non-Veg' ? 'bg-red-500/80' :
                        item.dietary_badge === 'Vegan' ? 'bg-green-600/80' : 'bg-orange-500/80'
                      } backdrop-blur-md`}>
                      {item.dietary_badge}
                    </span>
                  )}

                  {/* High Rating Badge */}
                  {item.average_rating >= 4.5 && (
                    <span className="bg-yellow-500/90 text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-lg border border-yellow-300/50 flex items-center gap-1 backdrop-blur-md">
                      ⭐ Popular
                    </span>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-start mb-2 px-1">
                <h3 className="font-bold text-xl text-white leading-tight font-serif tracking-wide">{item.name}</h3>
                <div className="flex flex-col items-end gap-1">
                  <span className="font-bold text-lg text-white/90">
                    {item.variants?.length > 0 ? `From ₹${Math.min(...item.variants.map(v => v.price))}` : `₹${item.price}`}
                  </span>
                </div>
              </div>

              <p className="text-gray-400 text-sm mb-6 line-clamp-2 leading-relaxed px-1 font-medium">{item.description}</p>

              {/* BUTTON - HIDE IF FREE PLAN */}
              {restaurant.is_pro && (
                <button
                  disabled={!item.is_available}
                  onClick={() => handleItemClick(item)}
                  className="w-full py-4 text-black rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_25px_rgba(255,255,255,0.3)] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none bg-white/90 backdrop-blur-sm"
                >
                  <span>{item.is_available ? '+' : '🚫'}</span>
                  {item.is_available ? (item.variants?.length > 0 ? 'Select Size' : 'Add to Order') : 'Sold Out'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 6. FOOTER SECTION - Dark Mode */}
      <footer className="glass border-t border-white/5 mt-12 pb-28 pt-12 text-center text-gray-400">
        <div className="container mx-auto px-4">
          <h3 className="font-bold text-white text-lg mb-2 font-serif">Connect With Us</h3>
          <p className="text-gray-500 text-sm mb-6">Follow us for updates & specials! 📸</p>

          <div className="flex justify-center gap-4 mb-8">
            {instagram && (
              <a href={instagram} target="_blank" rel="noreferrer" className="glass hover:bg-white/10 p-3 rounded-full transition-colors border border-white/5">
                <img src="https://cdn-icons-png.flaticon.com/512/87/87390.png" alt="Insta" className="w-6 h-6 invert opacity-80" />
              </a>
            )}
            {facebook && (
              <a href={facebook} target="_blank" rel="noreferrer" className="glass hover:bg-white/10 p-3 rounded-full transition-colors border border-white/5">
                <img src="https://cdn-icons-png.flaticon.com/512/44/44646.png" alt="FB" className="w-6 h-6 invert opacity-80" />
              </a>
            )}
            {!instagram && !facebook && (
              <span className="text-gray-600 text-sm italic">No social links added yet.</span>
            )}
          </div>

          <div className="text-xs text-gray-600 font-medium">
            <p>© {new Date().getFullYear()} {restaurant.name}. All rights reserved.</p>
            {!restaurant.is_pro && (
              <p className="mt-2 text-gray-600">
                Powered by <span className="font-bold text-indigo-400">TableAR</span>
              </p>
            )}
          </div>
        </div>
      </footer>

      {/* 7. VARIANT SELECTOR */}
      {
        selectedItemForVariant && restaurant.is_pro && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-end md:items-center justify-center p-4 animate-fade-in">
            <div className="bg-white w-full max-w-sm rounded-2xl p-6 animate-slide-up relative">
              <button onClick={() => setSelectedItemForVariant(null)} className="absolute top-4 right-4 text-gray-400 font-bold text-xl hover:text-black">✕</button>
              <h3 className="text-xl font-bold mb-1 text-gray-900">{selectedItemForVariant.name}</h3>
              <p className="text-gray-500 text-sm mb-6">Select a size option</p>

              <div className="space-y-3">
                {selectedItemForVariant.variants.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => handleVariantSelect(selectedItemForVariant, v)}
                    className="w-full flex justify-between items-center p-4 border border-gray-200 rounded-xl hover:border-black hover:bg-gray-50 transition-all active:scale-95 group"
                  >
                    <span className="font-bold text-gray-800">{v.name}</span>
                    <span className="font-bold group-hover:scale-110 transition-transform" style={{ color: themeColor }}>₹{v.price}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      }

      {/* 8. SMART UPSELL MODAL */}
      {
        showUpsellModal && restaurant.is_pro && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white w-full max-w-sm rounded-3xl p-0 animate-slide-up relative overflow-hidden shadow-2xl">
              {/* Header */}
              <div className="bg-gradient-to-br from-orange-500 to-red-600 p-6 text-white text-center">
                <h3 className="text-2xl font-black mb-1">Make it a Meal! 🍔</h3>
                <p className="text-white/90 text-sm font-medium">
                  Great choice! These go perfectly with your <span className="font-bold underline">{upsellItem?.name}</span>.
                </p>
              </div>

              {/* Candidates List */}
              <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
                {upsellCandidates.map(candidate => (
                  <div key={candidate.id} className="flex items-center gap-4 bg-gray-50 p-3 rounded-2xl border border-gray-100 hover:border-orange-200 transition-colors">
                    <img src={candidate.image_url || 'https://via.placeholder.com/60'} alt={candidate.name} className="w-16 h-16 rounded-xl object-cover bg-white shadow-sm" />
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 leading-tight">{candidate.name}</h4>
                      <p className="text-gray-500 text-sm font-bold mt-1">₹{candidate.price}</p>
                    </div>
                    <button
                      onClick={() => handleAddUpsell(candidate)}
                      className="bg-black text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg hover:bg-gray-800 active:scale-95 transition-all"
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>

              {/* Footer Actions */}
              <div className="p-4 border-t border-gray-100 bg-gray-50">
                <button onClick={closeUpsell} className="w-full py-3 font-bold text-gray-400 hover:text-black transition-colors">
                  No thanks, continue to menu
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* 9. TRACK ORDERS BUTTON */}
      {
        activeOrderIds.length > 0 && !showOrderTracker && restaurant.is_pro && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-full px-4 max-w-md animate-bounce-in">
            <button
              onClick={() => setShowOrderTracker(true)}
              className="w-full bg-black text-white py-4 rounded-xl font-bold shadow-2xl flex items-center justify-center gap-2 border-2 border-white/20 backdrop-blur-md"
            >
              <span>🧾</span> Track {activeOrderIds.length} Active Order{activeOrderIds.length > 1 ? 's' : ''}
            </button>
          </div>
        )
      }



      {/* 10. FLOATING CART BUTTON (Bottom Right) */}
      {
        restaurant.is_pro && (
          <button
            onClick={() => setShowCart(true)}
            className="fixed bottom-6 right-6 z-50 p-4 bg-white text-gray-900 rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] hover:-translate-y-1 transition-all group border border-white/20 animate-bounce-in"
          >
            <span className="text-3xl filter drop-shadow-sm">🛍️</span>
            {cartItems.reduce((acc, item) => acc + item.quantity, 0) > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-lg animate-bounce-short border-2 border-white">
                {cartItems.reduce((acc, item) => acc + item.quantity, 0)}
              </span>
            )}
          </button>
        )
      }

      {/* 11. CART MODAL */}
      {
        showCart && restaurant.is_pro && (
          <Cart
            cartItems={cartItems}
            onClose={() => setShowCart(false)}
            onUpdateCart={setCartItems}
            restaurantId={restaurantId}
            tableNumber={tableNumber}
            themeColor={themeColor}
            onOrderPlaced={handleOrderPlaced}
            restaurant={restaurant}
          />
        )
      }

      {/* 12. ORDER TRACKER MODAL (With Rating Logic) */}
      {
        showOrderTracker && restaurant.is_pro && (
          <OrderTracker
            orderIds={activeOrderIds}
            onClose={() => setShowOrderTracker(false)}
            restaurantId={restaurantId}
          />
        )
      }
    </div >
  );
};

export default PublicMenu;